import type { OrderBy } from '@/lib/graphql-query-builder';

/**
 * Manage Attendance: every organization runs the same flow; what a reviewer may do comes from their
 * permissions (`ATTENDANCE.REVIEW_CORRECTION`, `.WAIVE_CORRECTION_FINE`, `.EXPORT`) and which
 * employees they see from the department permissions (`useDepartmentScope`). Approve / reject / waive
 * each go through their own workflow, which re-checks the reviewer (admin, or head of the correction's
 * department; never their own request). Bulk review calls the same workflow once per request.
 */

export interface AttendanceRecord {
  ItemId: string;
  AttendanceDate: string;
  StartTimeUtc?: string;
  EndTimeUtc?: string;
  /** The organization's IANA time zone the day was recorded in, e.g. `Asia/Dhaka`. */
  Timezone?: string;
  UserId: string;
  DisplayName?: string;
  DepartmentId?: string;
  DepartmentName?: string;
  DepartmentKey?: string;
  TotalWorkTimeInSecond?: number;
  TotalBreakTimeInSecond?: number;
  TotalClockTimeInSecond?: number;
  ExpectedWorkingHourInSecond?: number;
  OverTimeInSecond?: number;
  /** Clocked in now (see `isOpenRecord`); absent on records saved before the field existed. */
  IsOpen?: boolean;
  /** Closed by the scheduler (FORCE_CLOCK_OUT_* rules). */
  IsForceClockOut?: boolean;
  /** Where the day was worked: `WFH` (clocked in from the app, or an approved correction) or `WFO`
   * (imported from the office door / device log). Absent on records saved before the field existed. */
  Status?: 'WFH' | 'WFO';
  BreakTimeDetails?:
    { StartTimeUtc: string; EndTimeUtc?: string | null; Type?: string | null }[] | null;
}

/** One employee on one day of the roster: their day's record, or `null` when they have none. */
export interface RosterRow {
  employee: {
    UserId: string;
    DisplayName?: string;
    Email?: string;
    DepartmentId?: string;
    DepartmentName?: string;
    Designation?: string;
  };
  record: AttendanceRecord | null;
}

export interface AttendancesPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: AttendanceRecord[];
}

export interface GetAttendancesParams {
  pageNo: number;
  pageSize: number;
  filter?: Record<string, unknown>;
  /** Defaults to newest day first. */
  order?: OrderBy[];
}

export type CorrectionStatus = 'Pending' | 'Approved' | 'Rejected';

export interface AttendanceCorrection {
  ItemId: string;
  /** Empty for a day the employee never clocked in until the correction's approval creates it. */
  AttendanceId?: string;
  /** IAM user id of the employee; the name is looked up from it. */
  UserId: string;
  DepartmentId?: string;
  DepartmentName?: string;
  DepartmentKey?: string;
  CorrectionDate: string;
  StartTimeUtc: string;
  EndTimeUtc: string;
  /** The organization's IANA time zone the correction was made in, e.g. `Asia/Dhaka`. */
  Timezone?: string;
  /** The corrected breaks; `null` from the gateway when there are none. */
  BreakTimeDetails?: { StartTimeUtc?: string | null; EndTimeUtc?: string | null }[] | null;
  Comment?: string;
  Status: CorrectionStatus;
  Note?: string;
  Fine?: number;
  /** Platform stamps (UTC, shown in the row's `Timezone`): when it was requested, and when it was
   * last changed — the approval or rejection, or a later fine waive. */
  CreatedDate?: string;
  LastUpdatedDate?: string;
  /** IAM user id of the approver or rejecter; the name is looked up from it. */
  ActionTakenBy?: string;
}

export interface AttendanceCorrectionsPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: AttendanceCorrection[];
}

export interface GetAttendanceCorrectionsParams {
  pageNo: number;
  pageSize: number;
  filter?: Record<string, unknown>;
  /** Defaults to latest corrected day first. */
  order?: OrderBy[];
}

/** Filters the attendance and correction-request lists share: keyword (employee name; corrections
 * store none, so it is matched through the employees it finds), department, (corrections only)
 * status, and (attendance only) the organization day "YYYY-MM-DD". */
export interface ManageAttendanceFilters {
  keyword: string;
  departmentId: string;
  status: string;
  date: string;
}

export const EMPTY_MANAGE_ATTENDANCE_FILTERS: ManageAttendanceFilters = {
  keyword: '',
  departmentId: '',
  status: '',
  date: '',
};
